import { css } from '@styled-system/css';
import { Select as AntdSelect, App, Cascader, message as messageStatic } from 'antd';
import {
  Award,
  Copy,
  Euro,
  ExternalLink,
  Folder as FolderIcon,
  Gamepad2,
  Globe,
  Grid3x3,
  HardDrive,
  Hash,
  Languages,
  LayoutGrid,
  Link as LinkIcon,
  Lock,
  Package,
  Palette,
  Ruler,
  ShieldCheck,
  Shirt,
  Sparkles,
  X,
} from 'lucide-react';
import { type FC, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { QuotaReachedModal } from '@/features/credits/QuotaReachedModal';
import { useCredits } from '@/features/credits/useCredits';
import { ChipMultiPicker } from '@/features/productSearch/components/ChipMultiPicker';
import { VestiaireFilterPanel } from '@/features/productSearch/vestiaire/VestiaireFilterPanel';
import { VestiaireMatchPreview } from '@/features/productSearch/vestiaire/VestiaireMatchPreview';
import { useVestiaireSearchForm } from '@/features/productSearch/vestiaire/useVestiaireSearchForm';
import { useVestiaireSearchUrl } from '@/features/productSearch/vestiaire/useVestiaireSearchUrl';
import { hasBlockingVestiaireSubmitError } from '@/features/productSearch/vestiaire/vestiaireCriteria';
import { omissionsText } from '@/features/productSearch/vestiaire/vestiaireUrlParts';
import { useVestiaireLocked } from '@/features/subscription/hooks/useVestiaireAccess';
import { creationPlatform } from '@/features/subscription/vestiaireAccess';
import type { AnalysisPlatform, VestiaireSearchCriteriaInput } from '@/shared/api/types/generated';
import { PlatformBadge } from '@/shared/components/analysis/PlatformBadge';
import { PlatformLogo } from '@/shared/components/analysis/PlatformLogo';
import { VestiaireLockedChip } from '@/shared/components/analysis/VestiaireLockedChip';
import { Accordion } from '@/shared/components/ui/Accordion';
import { Button } from '@/shared/components/ui/Button';
import { ChipToggle } from '@/shared/components/ui/ChipToggle';
import { FormField } from '@/shared/components/ui/FormField';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { Spinner } from '@/shared/components/ui/Spinner';
import { QUOTA_ERROR_MESSAGE } from '@/shared/hooks/analysis/mutations';
import { useGetVintedCountriesQuery } from '@/shared/hooks/queries/generated';
import type { KeywordSearchOperator, SearchCriteriaInput } from '@/shared/hooks/queries/generated';
import {
  useGetProductByIdQuery,
  useGetVintedCatalogFiltersQuery,
  useParseVintedCatalogUrlQuery,
  usePreviewAnalysisMatchCountQuery,
  useSearchVintedCatalogFilterOptionsQuery,
} from '@/shared/hooks/queries/generated';
import { useIsMobile } from '@/shared/hooks/useIsMobile';
import { useVintedOptions } from '@/shared/hooks/useVintedOptions';
import type { SelectOption } from '@/shared/types/options';
import {
  buildSimilarityCriteria,
  hasSimilaritySource,
  matchPreviewPrefix,
  readSimilaritySource,
} from '@/shared/utils/analysis/similarity';
import { localizeCountryName } from '@/shared/utils/localizeCountry';
import { isVintedCatalogUrl } from '@/shared/utils/parseVintedUrl';
import { type UploadedSearchImage, getSearchImagePublicUrl, uploadSearchImage } from '@/shared/utils/searchImageUpload';

import { MobileCategoryDrilldown } from './MobileCategoryDrilldown';
import { SimilaritySearchSection } from './SimilaritySearchSection';

type AntdTagRenderProps = { label: ReactNode; value: string; closable: boolean; onClose: () => void };

export interface SearchModalInput {
  title: string;
  /** Plateforme de l'onglet actif (Vinted en variante veille et par défaut). */
  platform: AnalysisPlatform;
  /** Critères structurés (ids Vinted) — absent en mode édition (rename seul) et sur Vestiaire. */
  criteria?: SearchCriteriaInput;
  /** Critères Vestiaire (ids VC) — seulement quand `platform` vaut 'vestiaire', en création. */
  vestiaireCriteria?: VestiaireSearchCriteriaInput;
  folderId?: string | null;
}

export interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (input: SearchModalInput, source?: 'url' | 'manual') => Promise<void>;
  /** 'edit' : affiche les inputs de la recherche en lecture seule, seul le nom est modifiable. */
  mode?: 'create' | 'edit';
  initialName?: string;
  initialUrl?: string;
  /** Pré-affecte la recherche créée à ce dossier (bouton « + » d'un dossier). */
  initialFolderId?: string | null;
  /** Dossiers serveur pour le sélecteur (fournis par la page). */
  folders?: { id: string; name: string; position: number }[];
  /**
   * Filtre de création (mode édition) : l'URL ne porte ni l'image ni la
   * description — on les lit ici pour les afficher en lecture seule.
   */
  initialParsedFilter?: Record<string, unknown> | null;
  /**
   * Vignette de l'analyse (mode édition). Pour une recherche par image, le
   * back y met justement l'URL publique de l'image de requête
   * (`publicUrlFor(imageKey)`) — source d'aperçu fiable sur tout navigateur.
   */
  initialImageUrl?: string | null;
  /**
   * 'veille' : mêmes critères, vocabulaire adapté (« Lancer la veille »),
   * sans dossier ni pré-calculateur d'articles (la veille suit un flux,
   * elle ne fige pas un snapshot à compter).
   */
  variant?: 'search' | 'veille';
  /** Onglet initial (défaut Vinted) ; en édition, il est figé sur la plateforme de l'analyse. */
  initialPlatform?: AnalysisPlatform;
  /** Filtre de création Vestiaire (mode édition d'une analyse VC), pour le préremplissage. */
  initialVestiaireFilter?: Record<string, unknown> | null;
}

// ============================================================================
// Layout styles
// ============================================================================

const grid = css({
  display: 'grid',
  gridTemplateColumns: '1fr 380px',
  gridTemplateAreas: '"main side"',
  height: 'full',
  minHeight: 0,
  // Narrow desktop : stack, ordre original (main puis side).
  '@media (max-width: 880px)': {
    gridTemplateColumns: '1fr',
    gridTemplateAreas: '"main" "side"',
  },
  // Mobile : on inverse pour mettre la card de droite (nom + aperçu URL)
  // EN HAUT et les accordions catégories EN DESSOUS. Single-scroll unifié
  // sur la grid (les colonnes désactivent leur overflow propre).
  '@media (max-width: 767px)': {
    gridTemplateColumns: '1fr',
    gridTemplateAreas: '"side" "main"',
    gridTemplateRows: 'auto auto',
    rowGap: '4',
    overflowY: 'auto',
    height: '100%',
  },
});

const leftColumn = css({
  gridArea: 'main',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  paddingY: '6',
  paddingX: '10',
  gap: '4',
  '@media (max-width: 767px)': {
    overflowY: 'visible',
    minHeight: 'auto',
    height: 'auto',
    paddingX: '4',
    paddingY: '4',
  },
});

const rightColumn = css({
  gridArea: 'side',
  paddingY: '8',
  paddingX: '8',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  overflowY: 'auto',
  '@media (max-width: 767px)': {
    overflowY: 'visible',
    minHeight: 'auto',
    height: 'auto',
    paddingX: '4',
    paddingY: '4',
  },
});

const rightCard = css({
  bg: 'surface.subtle',
  borderRadius: 'lg',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  paddingY: '8',
  paddingX: '8',
  display: 'flex',
  flexDirection: 'column',
  gap: '8',
  flex: 1,
  '@media (max-width: 767px)': {
    paddingY: '5',
    paddingX: '5',
    gap: '6',
    flex: 'unset',
  },
});

// Boîte d'aperçu du volume, collée en bas de la carte de droite (Vinted et Vestiaire)
const previewBox = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '1',
  paddingY: '5',
  paddingX: '6',
  bg: 'surface.default',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'lg',
  marginTop: 'auto',
});

const inlineGap = css({ display: 'flex', gap: '3', alignItems: 'flex-end' });

const errorText = css({ fontSize: 'caption', color: 'error.default' });

// Custom tag styles for AntD Cascader/Select tagRender — render as plain
// spans so AntD's own <Tag> styles don't override our DS tokens.
const dsCategoryTag = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2',
  marginInlineEnd: '2',
  paddingY: '1',
  paddingX: '3',
  borderRadius: 'sm',
  bg: 'success.soft',
  color: 'success.default',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'success.border',
  fontFamily: 'sans',
  fontSize: 'caption',
  fontWeight: 'medium',
  whiteSpace: 'nowrap',
});

const dsBrandTag = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2',
  marginInlineEnd: '2',
  paddingY: '1',
  paddingX: '3',
  borderRadius: 'sm',
  bg: 'info.soft',
  color: 'info.default',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'info.border',
  fontFamily: 'sans',
  fontSize: 'caption',
  fontWeight: 'medium',
  whiteSpace: 'nowrap',
});

const dsExcludedTag = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2',
  marginInlineEnd: '2',
  paddingY: '1',
  paddingX: '3',
  borderRadius: 'sm',
  bg: 'error.soft',
  color: 'error.default',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'error.border',
  fontFamily: 'sans',
  fontSize: 'caption',
  fontWeight: 'medium',
  whiteSpace: 'nowrap',
});

const tagCloseBtn = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'inherit',
  opacity: 0.65,
  padding: 0,
  _hover: { opacity: 1 },
});

// ============================================================================
// URL preview sub-component
// ============================================================================

const urlPreviewBox = css({
  bg: 'surface.default',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'md',
  padding: '4',
  display: 'flex',
  flexDirection: 'column',
  gap: '3',
});

const urlText = css({
  fontSize: 'caption',
  color: 'text.tertiary',
  fontFamily: 'mono',
  wordBreak: 'break-all',
  lineHeight: 1.5,
  textDecoration: 'none',
  _hover: { color: 'text.secondary' },
});

const urlActions = css({
  display: 'flex',
  gap: '2',
  justifyContent: 'flex-end',
});

interface UrlPreviewProps {
  url: string | null;
  onCopy: () => void;
}

const DEFAULT_VINTED_URL = 'https://www.vinted.fr/catalog?page=1';

const UrlPreview: FC<UrlPreviewProps> = ({ url, onCopy }) => {
  const { t } = useTranslation();
  const displayUrl = url ?? DEFAULT_VINTED_URL;
  return (
    <div className={urlPreviewBox}>
      <a href={displayUrl} target="_blank" rel="noopener noreferrer" className={urlText}>
        {displayUrl}
      </a>
      <div className={urlActions}>
        <Button
          variant="ghost"
          size="sm"
          icon={<ExternalLink size={12} strokeWidth={1.75} />}
          onClick={() => {
            window.open(displayUrl, '_blank');
          }}
        >
          {t('searchModal.open')}
        </Button>
        <Button variant="ghost" size="sm" icon={<Copy size={12} strokeWidth={1.75} />} onClick={onCopy}>
          {t('searchModal.copy')}
        </Button>
      </div>
    </div>
  );
};

// ============================================================================
// Helpers
// ============================================================================

const numOrUndefined = (v: string): number | undefined => {
  if (!v.trim()) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * Parse client-side d'une URL catalogue Vinted vers les champs du formulaire.
 * Couvre les params réellement générés par Vinted :
 * catalog[], brand_ids[]/brand_id[], price_from/to, status_ids[], search_text.
 * Les facettes rares (couleurs, matières…) sont ignorées — l'URL finale les
 * conservera si l'utilisateur soumet sans les toucher.
 */
interface ParsedVintedUrl {
  catalogIds: number[];
  brandIds: string[];
  statusIds: string[];
  priceMin?: number;
  priceMax?: number;
  searchText?: string;
}

const parseVintedCatalogUrl = (raw: string): ParsedVintedUrl => {
  const u = new URL(raw.trim());
  const p = u.searchParams;
  const catalogIds = p
    .getAll('catalog[]')
    .map((v) => parseInt(v, 10))
    .filter((n) => !isNaN(n));
  const brandIds = [...p.getAll('brand_ids[]'), ...p.getAll('brand_id[]')].filter(Boolean);
  const statusIds = p.getAll('status_ids[]').filter(Boolean);
  const priceMin = p.get('price_from');
  const priceMax = p.get('price_to');
  const searchText = p.get('search_text');
  return {
    catalogIds,
    brandIds,
    statusIds,
    priceMin: priceMin != null ? Number(priceMin) : undefined,
    priceMax: priceMax != null ? Number(priceMax) : undefined,
    searchText: searchText ?? undefined,
  };
};

// ============================================================================
// Main component
// ============================================================================

export const SearchModal: FC<SearchModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode = 'create',
  initialName,
  initialUrl,
  initialFolderId = null,
  folders = [],
  initialParsedFilter = null,
  initialImageUrl = null,
  variant = 'search',
  initialPlatform = 'vinted',
  initialVestiaireFilter = null,
}) => {
  const { t, i18n } = useTranslation();
  const isVeille = variant === 'veille';
  const isEdit = mode === 'edit';
  // La veille ne suit que Vinted : jamais d'onglet, plateforme forcée.
  const openingPlatform: AnalysisPlatform = isVeille ? 'vinted' : initialPlatform;
  const [platform, setPlatform] = useState<AnalysisPlatform>(openingPlatform);
  const isVinted = platform === 'vinted';
  const isVestiaire = platform === 'vestiaire';
  // Recherche VC réservée aux plans qui l'incluent : puce verrouillée en création. Onglet VC actif
  // alors que l'accès manque (plateforme d'ouverture, réponse arrivée après coup) ⇒ retour à Vinted.
  // L'édition (nom seul) d'une analyse VC existante n'est pas une création : jamais rabattue.
  const vestiaireLocked = useVestiaireLocked();
  useEffect(() => {
    if (!isOpen || isEdit) return;
    const allowed = creationPlatform(platform, vestiaireLocked);
    if (allowed !== platform) setPlatform(allowed);
  }, [isOpen, isEdit, platform, vestiaireLocked]);
  // Erreurs du panneau Vestiaire affichées seulement après une première tentative d'envoi.
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // Préremplissage UNE fois par instance du hook : `reset()` à chaque ouverture (cf plus bas).
  // Requêtes VC (arbre, titres) seulement quand elles servent : onglet VC ouvert, ou édition d'une
  // analyse VC. Une modale veille ou Vinted seule ne les émet jamais.
  const vestiaireSearch = useVestiaireSearchForm({
    initialFilter: isEdit ? initialVestiaireFilter : null,
    enabled: isOpen && (isVestiaire || (isEdit && initialVestiaireFilter != null)),
  });
  const vestiaireUrl = useVestiaireSearchUrl(vestiaireSearch.criteria, isOpen && isVestiaire);
  // Try AntD App for toasts; fall back to static API if outside <App>
  let appMessage: typeof messageStatic;
  try {
    appMessage = App.useApp().message as unknown as typeof messageStatic;
  } catch {
    appMessage = messageStatic;
  }

  const isMobile = useIsMobile();
  const credits = useCredits();

  // 26 pays filtrables, groupés par région pour le menu. Query en échec
  // (back pas encore déployé) -> section masquée, countryIds jamais envoyé.
  const { data: countriesData } = useGetVintedCountriesQuery(undefined, {
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });
  const countryRegions = useMemo(() => {
    const countries = countriesData?.getVintedCountries ?? [];
    // `others` n'apparaît sur aucun pays (classe interne) — ordre d'affichage fixe
    const order = ['europeWest', 'europeEast', 'unitedKingdom', 'northAmerica'] as const;
    return order
      .map((region) => ({ region, countries: countries.filter((c) => c.region === region) }))
      .filter((group) => group.countries.length > 0);
  }, [countriesData]);
  const [quotaModalOpen, setQuotaModalOpen] = useState(false);

  // Options locales restantes : l'arbre catégories (Cascader) + marques
  // populaires en pré-remplissage — le reste vient des facettes serveur.
  const { categories: categoryOptions, isLoading: categoriesLoading, marques, getCategoryPaths } = useVintedOptions();

  // ─── Form state ─────────────────────────────────────────────────────────
  const [nom, setNom] = useState('');
  const [folderId, setFolderId] = useState<string | null>(null);
  const folderOptions = useMemo(
    () => [
      { label: t('relaunch.noFolder'), value: '__none__' },
      ...[...folders].sort((a, b) => a.position - b.position).map((f) => ({ label: f.name, value: f.id })),
    ],
    [folders, t],
  );
  const [prixMin, setPrixMin] = useState('');
  const [prixMax, setPrixMax] = useState('');
  const [categoriesPaths, setCategoriesPaths] = useState<string[][]>([]);
  const [marquesValue, setMarquesValue] = useState<string[]>([]);
  const [etatsValue, setEtatsValue] = useState<string[]>([]);
  const [taillesValue, setTaillesValue] = useState<string[]>([]);
  const [couleursValue, setCouleursValue] = useState<string[]>([]);
  const [matieresValue, setMatieresValue] = useState<string[]>([]);
  const [motifsValue, setMotifsValue] = useState<string[]>([]);
  const [modelesValue, setModelesValue] = useState<string[]>([]);
  const [plateformesValue, setPlateformesValue] = useState<string[]>([]);
  const [classementsValue, setClassementsValue] = useState<string[]>([]);
  const [stockagesValue, setStockagesValue] = useState<string[]>([]);
  const [languesValue, setLanguesValue] = useState<string[]>([]);
  const [simlocksValue, setSimlocksValue] = useState<string[]>([]);
  const [motsCles, setMotsCles] = useState<string[]>([]);
  // ET (défaut, historique) : tous les mots-clés · OU : au moins un
  const [motsClesOperator, setMotsClesOperator] = useState<KeywordSearchOperator>('and');
  const [motsClesExclus, setMotsClesExclus] = useState<string[]>([]);
  const [marquesExcluesValue, setMarquesExcluesValue] = useState<string[]>([]);
  // 2026-09-02 : pays du VENDEUR (ids) — menu groupé par région, envoi à plat
  const [paysValue, setPaysValue] = useState<string[]>([]);
  // Recherche par image (uploadée immédiatement : l'URL présignée expire en
  // 300 s) + description sémantique libre
  const [searchImage, setSearchImage] = useState<UploadedSearchImage | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [description, setDescription] = useState('');
  const [openSections, setOpenSections] = useState<string[]>([]);
  // Sections à ouvrir dans le panneau Vestiaire (il gère son propre accordéon) : `seq` rejoue la
  // demande même quand la liste ne change pas (collage d'une deuxième image).
  const [vestiaireOpenRequest, setVestiaireOpenRequest] = useState<{ sections: string[]; seq: number }>({
    sections: [],
    seq: 0,
  });
  const requestVestiaireSections = useCallback((sections: string[]) => {
    setVestiaireOpenRequest((previous) => ({ sections, seq: previous.seq + 1 }));
  }, []);

  // ─── UI state ───────────────────────────────────────────────────────────
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [vintedUrl, setVintedUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [urlImportUsed, setUrlImportUsed] = useState(false);
  // URL collée puis « Créer » sans « Importer » : l'import met l'état à jour, mais la closure de
  // `handleSubmit` en cours lit encore les critères d'AVANT (ils ne changent qu'au rendu suivant).
  // L'envoi est donc relancé une fois le rendu fait, avec les critères importés.
  const [submitAfterImport, setSubmitAfterImport] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);

  // Brand options (recherche serveur débouncée + import prefill)
  const [brandOptions, setBrandOptions] = useState<SelectOption[]>(marques);
  const brandSearchTimer = useRef<number | null>(null);

  // ─── Facettes serveur : les filtres POSSIBLES pour les catégories choisies
  // (logique Autocop : catégorie d'abord, les combinaisons apparaissent) ───
  const selectedCatalogIds = useMemo(
    () => categoriesPaths.map((p) => Number(p[p.length - 1])).filter((n) => Number.isFinite(n)),
    [categoriesPaths],
  );
  const { data: catalogFiltersData, isLoading: catalogFiltersLoading } = useGetVintedCatalogFiltersQuery(
    { mainCatalogIds: selectedCatalogIds },
    { enabled: isOpen && isVinted && selectedCatalogIds.length > 0, staleTime: 5 * 60 * 1000 },
  );
  const serverFilters = useMemo(() => {
    const map: Record<string, SelectOption[]> = {};
    const hexById: Record<string, string> = {};
    for (const filter of catalogFiltersData?.getVintedCatalogFilters ?? []) {
      const flat = filter.options.flatMap((option) =>
        option.children?.length
          ? option.children.map((child) => ({
              id: child.id,
              title: child.title,
              itemsCount: child.itemsCount ?? null,
              hex: null as string | null,
            }))
          : [{ id: option.id, title: option.title, itemsCount: option.itemsCount ?? null, hex: option.hex ?? null }],
      );
      map[filter.type] = flat.map((option) => ({ label: option.title, value: String(option.id) }));
      for (const option of flat) {
        if (option.hex) hexById[String(option.id)] = option.hex.startsWith('#') ? option.hex : `#${option.hex}`;
      }
    }
    return { map, hexById };
  }, [catalogFiltersData]);

  // ─── Application d'une URL Vinted sur les champs du formulaire ─────────
  const applyUrlToFields = async (raw: string): Promise<number> => {
    const parsed = parseVintedCatalogUrl(raw);
    let count = 0;

    if (parsed.catalogIds.length) {
      const paths = getCategoryPaths(parsed.catalogIds);
      setCategoriesPaths(paths.map((p) => p.map(String)));
      count++;
    }
    if (parsed.priceMin != null) {
      setPrixMin(String(parsed.priceMin));
      count++;
    }
    if (parsed.priceMax != null) {
      setPrixMax(String(parsed.priceMax));
      count++;
    }
    if (parsed.statusIds.length) {
      setEtatsValue(parsed.statusIds);
      count++;
    }
    if (parsed.searchText) {
      setMotsCles(parsed.searchText.split(/\s+/).filter(Boolean));
      count++;
    }
    // ⚠️ Bloc marques EN DERNIER : c'est le seul qui `await` le réseau (résolution des titres).
    // Placé avant, il retardait priceMin/priceMax/statusIds/searchText derrière l'aller-retour —
    // sur réseau lent ces familles synchrones n'apparaissaient qu'après, et jamais si le fetch pend.
    if (parsed.brandIds.length) {
      setMarquesValue(parsed.brandIds);
      // Titres de marques par l'API — le parse des ids reste CLIENT (cf docblock de la tâche).
      // ⚠️ Dégradation volontaire : réseau en échec ou id sans titre ⇒ `searchModal.brandFallback`
      // (« Marque #123 »). L'import reste fonctionnel, seul le libellé est moins lisible.
      let titlesById = new Map<string, string>();
      try {
        const res = await useParseVintedCatalogUrlQuery.fetcher({ catalogUrl: raw })();
        titlesById = new Map((res.parseVintedCatalogUrl.brands ?? []).map((b) => [String(b.id), b.title]));
      } catch {
        // silencieux : le repli ci-dessous suffit, et une URL sans critère fait LEVER le serveur
        // alors que le parse client, lui, a déjà réussi.
      }
      const brandOpts = parsed.brandIds.map((id) => ({
        label: titlesById.get(id) ?? t('searchModal.brandFallback', { id }),
        value: id,
      }));
      const selectedIds = new Set(parsed.brandIds);
      const popular = marques.filter((m) => !selectedIds.has(m.value));
      setBrandOptions([...brandOpts, ...popular]);
      count++;
    }

    return count;
  };

  // ─── Reset on open (+ pré-remplissage en mode édition) ──────────────────
  useEffect(() => {
    if (!isOpen) return;

    setPlatform(openingPlatform);
    setSubmitAttempted(false);
    setUrlImportUsed(false);
    setSubmitAfterImport(false);
    setUrlError('');
    setVintedUrl('');
    setErrors({});
    setNom('');
    setFolderId(initialFolderId ?? null);
    setPrixMin('');
    setPrixMax('');
    setCategoriesPaths([]);
    setMarquesValue([]);
    setEtatsValue([]);
    setTaillesValue([]);
    setCouleursValue([]);
    setMatieresValue([]);
    setMotifsValue([]);
    setModelesValue([]);
    setPlateformesValue([]);
    setClassementsValue([]);
    setStockagesValue([]);
    setLanguesValue([]);
    setSimlocksValue([]);
    setMotsCles([]);
    setMotsClesOperator('and');
    setMotsClesExclus([]);
    setMarquesExcluesValue([]);
    setPaysValue([]);
    setSearchImage(null);
    setImageUploading(false);
    setDescription('');
    setBrandOptions(marques);
    setOpenSections(['categories']);

    if (isEdit) {
      setNom(initialName ?? '');
      // L'URL ne porte ni image ni description : on les lit du filtre de création de LA
      // plateforme (parsedFilter sur Vinted, vestiaireFilter sur VC)
      const pf = initialParsedFilter;
      const similarityFilter = openingPlatform === 'vestiaire' ? initialVestiaireFilter : pf;
      const hasSimilarity = hasSimilaritySource(similarityFilter);
      const storedDescription = readSimilaritySource(similarityFilter).description;
      if (storedDescription != null) setDescription(storedDescription);
      // Le filtre pays ne vit jamais dans l'URL Vinted : lecture directe
      const pfCountryIds = Array.isArray(pf?.countryIds)
        ? (pf.countryIds as unknown[]).filter((v): v is number => typeof v === 'number')
        : [];
      if (pfCountryIds.length) setPaysValue(pfCountryIds.map(String));
      // Analyse Vestiaire : son URL n'est pas une URL catalogue Vinted, le panneau VC se
      // préremplit depuis `initialVestiaireFilter` (hook), rien à lire ici.
      if (openingPlatform === 'vestiaire') {
        setGeneratedUrl(null);
        if (hasSimilarity) requestVestiaireSections(['image']);
      } else if (initialUrl) {
        setGeneratedUrl(initialUrl);
        void applyUrlToFields(initialUrl).then((count) => {
          // Ouvre les sections qui ont des valeurs pour montrer la recherche
          if (count > 0 || hasSimilarity) {
            setOpenSections([
              'categories',
              'marques',
              'prix',
              'motsCles',
              'etats',
              ...(hasSimilarity ? ['image'] : []),
              ...(Array.isArray(pf?.countryIds) && pf.countryIds.length ? ['pays'] : []),
            ]);
          }
        });
      } else if (hasSimilarity) {
        setOpenSections(['image']);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    isEdit,
    initialName,
    initialUrl,
    initialFolderId,
    initialParsedFilter,
    initialVestiaireFilter,
    openingPlatform,
  ]);

  // Formulaire Vestiaire à chaque ouverture : vide en création, préremplissage rejoué en
  // édition (le hook ne préremplit qu'une fois par instance, et la modale d'édition de la
  // page d'analyse reste montée entre deux ouvertures). `reset` change d'identité à l'arrivée
  // de l'arbre : il ne doit PAS relancer cet effet, le hook préremplit alors de lui-même.
  const resetVestiaire = useRef(vestiaireSearch.reset);
  resetVestiaire.current = vestiaireSearch.reset;
  useEffect(() => {
    if (isOpen) resetVestiaire.current();
  }, [isOpen]);

  // ─── Répare les chemins de catégorie dégradés une fois l'arbre disponible ──
  // `getCategoryPaths` est synchrone et lit une carte construite depuis l'arbre catalogue (API). Si
  // `applyUrlToFields` tourne AVANT que l'arbre soit chargé (ouverture en mode édition, ou arbre en
  // échec), chaque id dégrade en chemin `[id]` (cf `useVintedOptions`) : le Cascader affiche alors un
  // tag brut sans case cochée. Dès que l'arbre arrive, on re-dérive — mais SEULEMENT si un chemin
  // courant est encore incomplet (longueur 1 alors que la carte connaît désormais un chemin plus long
  // pour cette feuille), sinon cet effet boucle sur lui-même à chaque render.
  // ⚠️ `selectedCatalogIds` prend le DERNIER élément de chaque chemin : même dégradé, les critères
  // soumis et les facettes restent corrects — ce filet n'est ni cassé ni remplacé par cet effet.
  useEffect(() => {
    if (categoriesLoading || categoriesPaths.length === 0) return;
    const leafIds = categoriesPaths.map((p) => Number(p[p.length - 1]));
    const resolved = getCategoryPaths(leafIds);
    const isDegraded = categoriesPaths.some((path, index) => path.length === 1 && resolved[index].length > 1);
    if (!isDegraded) return;
    setCategoriesPaths(resolved.map((p) => p.map(String)));
  }, [categoriesLoading, categoriesPaths, getCategoryPaths]);

  // Image présente dans le filtre d'une recherche existante (mode édition).
  // Aperçu : la vignette de l'analyse (le back y met l'URL publique de
  // l'image de requête quand imageKey est défini) ; repli sur la
  // correspondance locale clé -> URL mémorisée à l'upload.
  // Filtre de LA plateforme de l'analyse éditée (vestiaireFilter sur VC, parsedFilter sinon).
  const editSimilarity = readSimilaritySource(
    isEdit ? (openingPlatform === 'vestiaire' ? initialVestiaireFilter : initialParsedFilter) : null,
  );
  const editImageKey = editSimilarity.imageKey;
  const editImageUrl = editImageKey ? (initialImageUrl ?? getSearchImagePublicUrl(editImageKey)) : null;

  // Relance « articles similaires » : l'image de requête est celle de
  // l'article source — on la récupère côté serveur (marche partout). Vinted
  // seulement (getProductById) : sur VC, la vignette de l'analyse EST la photo
  // de l'article source (repli déjà prévu par la section).
  const editSourceProductId = editSimilarity.sourceProductId;
  const { data: sourceProductData } = useGetProductByIdQuery(
    { id: editSourceProductId ?? '' },
    { enabled: isOpen && !!editSourceProductId && isVinted, staleTime: Infinity, gcTime: 30 * 60 * 1000 },
  );
  const sourceProduct = sourceProductData?.getProductById;

  const handleCategoriesChange = useCallback((value: string[][]) => {
    setCategoriesPaths(value);
  }, []);

  // ─── URL d'aperçu construite directement depuis les ids ─────────────────
  const buildSearchUrl = useCallback(async (): Promise<string | null> => {
    const params = new URLSearchParams();
    for (const p of categoriesPaths) {
      const last = p[p.length - 1];
      if (last) params.append('catalog[]', last);
    }
    for (const id of marquesValue) params.append('brand_ids[]', id);
    for (const id of etatsValue) params.append('status_ids[]', id);
    for (const id of taillesValue) params.append('size_ids[]', id);
    for (const id of couleursValue) params.append('color_ids[]', id);
    for (const id of matieresValue) params.append('material_ids[]', id);
    for (const id of motifsValue) params.append('pattern_ids[]', id);
    for (const id of modelesValue) params.append('brand_collection_ids[]', id);
    for (const id of plateformesValue) params.append('video_game_platform_ids[]', id);
    for (const id of classementsValue) params.append('video_game_rating_ids[]', id);
    for (const id of stockagesValue) params.append('internal_memory_capacity_ids[]', id);
    for (const id of languesValue) params.append('language_book_ids[]', id);
    for (const id of simlocksValue) params.append('sim_lock_ids[]', id);
    const min = numOrUndefined(prixMin);
    const max = numOrUndefined(prixMax);
    if (min != null) params.set('price_from', String(min));
    if (max != null) params.set('price_to', String(max));
    if (motsCles.length) params.set('search_text', motsCles.join(' '));
    const qs = params.toString();
    return qs ? `https://www.vinted.fr/catalog?${qs}` : null;
  }, [
    categoriesPaths,
    marquesValue,
    prixMin,
    prixMax,
    etatsValue,
    taillesValue,
    couleursValue,
    matieresValue,
    motifsValue,
    modelesValue,
    plateformesValue,
    classementsValue,
    stockagesValue,
    languesValue,
    simlocksValue,
    motsCles,
  ]);

  // ─── Critères (ids) + aperçu du volume détecté (débounce 1 s) ───────────
  const criteria = useMemo((): SearchCriteriaInput => {
    const toNums = (values: string[]) => values.map(Number).filter((n) => Number.isFinite(n));
    const built: SearchCriteriaInput = {};
    if (selectedCatalogIds.length) built.catalogIds = selectedCatalogIds;
    if (marquesValue.length) built.brandIds = toNums(marquesValue);
    if (etatsValue.length) built.conditionIds = toNums(etatsValue);
    if (taillesValue.length) built.sizeIds = toNums(taillesValue);
    if (couleursValue.length) built.colorIds = toNums(couleursValue);
    if (matieresValue.length) built.materialIds = toNums(matieresValue);
    if (motifsValue.length) built.patternIds = toNums(motifsValue);
    if (modelesValue.length) built.brandCollectionIds = toNums(modelesValue);
    if (plateformesValue.length) built.videoGamePlatformIds = toNums(plateformesValue);
    if (classementsValue.length) built.videoGameRatingIds = toNums(classementsValue);
    if (stockagesValue.length) built.internalMemoryCapacityIds = toNums(stockagesValue);
    if (languesValue.length) built.languageBookIds = toNums(languesValue);
    if (simlocksValue.length) built.simLockIds = toNums(simlocksValue);
    const min = numOrUndefined(prixMin);
    const max = numOrUndefined(prixMax);
    if (min != null) built.priceMin = min;
    if (max != null) built.priceMax = max;
    if (motsCles.length) built.keywords = motsCles;
    // Omis => 'and' (comportement historique) : on n'envoie que le 'or'
    if (motsCles.length && motsClesOperator === 'or') built.keywordsOperator = 'or';
    if (motsClesExclus.length) built.excludedKeywords = motsClesExclus;
    if (marquesExcluesValue.length) built.excludedBrandIds = toNums(marquesExcluesValue);
    // Pays : ne compte pas comme critère obligatoire ; jamais émis si vide
    // (le serveur pré-déploiement ne connaît pas le champ)
    if (paysValue.length) built.countryIds = toNums(paysValue);
    // Similarité : image téléversée et/ou description sémantique — ne
    // dispense pas d'un critère classique (validation inchangée)
    return { ...built, ...buildSimilarityCriteria(searchImage?.key, description) };
  }, [
    selectedCatalogIds,
    marquesValue,
    etatsValue,
    taillesValue,
    couleursValue,
    matieresValue,
    motifsValue,
    modelesValue,
    plateformesValue,
    classementsValue,
    stockagesValue,
    languesValue,
    simlocksValue,
    prixMin,
    prixMax,
    motsCles,
    motsClesOperator,
    motsClesExclus,
    marquesExcluesValue,
    paysValue,
    searchImage,
    description,
  ]);

  // Critères Vestiaire : panneau VC + mêmes champs de similarité que Vinted (image, description)
  const vestiaireCriteria = useMemo(
    (): VestiaireSearchCriteriaInput => ({
      ...vestiaireSearch.criteria,
      ...buildSimilarityCriteria(searchImage?.key, description),
    }),
    [vestiaireSearch.criteria, searchImage, description],
  );

  const hasScopeNow = selectedCatalogIds.length > 0 || marquesValue.length > 0 || motsCles.length > 0;

  const [debouncedCriteria, setDebouncedCriteria] = useState<SearchCriteriaInput | null>(null);
  useEffect(() => {
    if (!isOpen || isEdit || isVeille || !hasScopeNow) {
      setDebouncedCriteria(null);
      return;
    }
    // Pas de fetch tant qu'on ajuste les filtres : on attend une pause
    const handle = window.setTimeout(() => setDebouncedCriteria(criteria), 1000);
    return () => window.clearTimeout(handle);
  }, [criteria, hasScopeNow, isOpen, isEdit, isVeille]);

  const { data: previewData, isFetching: previewLoading } = usePreviewAnalysisMatchCountQuery(
    { input: debouncedCriteria ?? {} },
    { enabled: debouncedCriteria != null && isVinted, staleTime: 60 * 1000, placeholderData: (prev) => prev },
  );
  const matchPreview = debouncedCriteria != null ? previewData?.previewAnalysisMatchCount : undefined;

  /**
   * LE NOMBRE AFFICHÉ EST LE TIRAGE — ce que l'analyse retiendra vraiment —, jamais le
   * total du périmètre. Sur un catalogue large les deux diffèrent de deux ordres de
   * grandeur (mesuré : 4 476 619 articles dans le périmètre, 35 028 analysés), et c'est
   * le second que l'utilisateur retrouvera dans son analyse : l'aperçu doit annoncer ce
   * que l'analyse rendra.
   *
   * ⚠️⚠️ `sampledCount` est NULL sur le chemin SQL, où `totalCount` EST déjà le tirage
   * (plafonné à 834 par créneau) — d'où le `??`, et non une lecture de `engine`.
   *
   * ⚠️⚠️ **LE `+` EST PILOTÉ PAR `capped` SEUL, DANS LES DEUX MOTEURS — et c'est ce qui
   * est juste dès lors qu'on affiche le TIRAGE.** `capped` signifie exactement « au moins
   * un créneau contient plus d'articles que l'analyse n'en retiendra », càd « le résultat
   * est TRONQUÉ » : c'est le signal que l'utilisateur doit voir, et il vaut aussi bien sur
   * le chemin exhaustif que sur le SQL.
   * ⚠️ Cette dérivation a été écrite un temps comme `!exhaustive && capped`, en
   * raisonnant sur l'arithmétique du nombre affiché (le tirage est exact, donc pas de
   * `+`) — décision du propriétaire de la retirer : on perdait le seul indicateur disant
   * que le périmètre est plus large que l'échantillon, qui est l'information la plus
   * actionnable de cet écran (ajouter un critère supprime la troncature).
   * ⚠️ Conséquence de lecture assumée : « +35 028 articles analysés » se lit « au moins
   * 35 028 », alors que l'analyse en retiendra exactement 35 028. Le `+` qualifie le
   * PÉRIMÈTRE, pas le compte analysé. Contrat :
   * docs/superpowers/api-changelog/2026-09-12-moteur-analyse-filter-service.md §3.
   */
  const previewCount = matchPreview ? (matchPreview.sampledCount ?? matchPreview.totalCount) : null;
  const previewPrefix = matchPreview ? matchPreviewPrefix(matchPreview) : '';

  // ─── URL preview (real-time, debounced 250ms) ──────────────────────────
  useEffect(() => {
    if (!isOpen || isEdit) return; // en édition, l'URL d'origine est figée
    let cancelled = false;
    const handle = window.setTimeout(async () => {
      const url = await buildSearchUrl();
      if (!cancelled) setGeneratedUrl(url);
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [isOpen, isEdit, buildSearchUrl]);

  // ─── URL import (parse 100% client) ─────────────────────────────────────
  /** Rend `true` si l'URL a été lue et appliquée au formulaire. */
  const handleUrlImport = async (): Promise<boolean> => {
    if (!vintedUrl.trim()) return false;

    if (!isVintedCatalogUrl(vintedUrl.trim())) {
      setUrlError(t('searchModal.urlInvalid'));
      return false;
    }

    setUrlError('');
    try {
      const count = await applyUrlToFields(vintedUrl.trim());
      setUrlImportUsed(true);
      if (count > 0) {
        appMessage.success(t('searchModal.urlImportSuccess', { count }));
      } else {
        appMessage.info(t('searchModal.urlImportNone'));
      }
      return true;
    } catch {
      setUrlError(t('searchModal.urlReadError'));
      return false;
    }
  };

  // ─── Recherche de marque serveur (partagée par inclusion ET exclusion) —
  // débounce 300 ms, préserve les options des deux sélections courantes ───
  const handleBrandSearch = (value: string) => {
    if (brandSearchTimer.current) window.clearTimeout(brandSearchTimer.current);
    brandSearchTimer.current = window.setTimeout(async () => {
      let results: SelectOption[];
      try {
        // Recherche serveur (scopée aux catégories, sinon racines)
        const scope = selectedCatalogIds.length
          ? selectedCatalogIds
          : categoryOptions.map((o) => Number(o.value)).filter((n) => Number.isFinite(n));
        const res = await useSearchVintedCatalogFilterOptionsQuery.fetcher({
          mainCatalogIds: scope,
          filterType: 'BRAND',
          query: value,
        })();
        results = res.searchVintedCatalogFilterOptions.map((o) => ({
          label: o.title,
          value: String(o.id),
        }));
      } catch {
        // ⚠️ `results = []` et NON un `catch` supprimé : sans assignation, `results.filter` (plus bas)
        // jetterait dans un callback async — rejet non capturé, et les options PÉRIMÉES resteraient
        // affichées. Une liste vide est un état honnête ; des options périmées sont un mensonge.
        results = [];
      }
      const keepIds = new Set([...marquesValue, ...marquesExcluesValue]);
      const selectedOptions = brandOptions.filter((o) => keepIds.has(o.value));
      const newOptions = results.filter((o) => !keepIds.has(o.value));
      setBrandOptions([...selectedOptions, ...newOptions]);
    }, 300);
  };

  // ─── Image de requête : upload immédiat (URL présignée valable 300 s) ───
  const handleImageSelect = async (file: File | undefined) => {
    if (!file) return;
    setImageUploading(true);
    try {
      const uploaded = await uploadSearchImage(file);
      setSearchImage(uploaded);
    } catch (error) {
      appMessage.error(error instanceof Error ? error.message : t('searchModal.imageUploadFailed'));
    } finally {
      setImageUploading(false);
    }
  };

  // ─── Collage direct d'une image (Ctrl/Cmd+V) n'importe où dans la popup ──
  useEffect(() => {
    if (!isOpen || isEdit) return;
    const onPaste = (event: ClipboardEvent) => {
      const file = Array.from(event.clipboardData?.items ?? [])
        .find((item) => item.kind === 'file' && item.type.startsWith('image/'))
        ?.getAsFile();
      if (!file) return;
      event.preventDefault();
      if (isVinted) setOpenSections((prev) => Array.from(new Set([...prev, 'image'])));
      else requestVestiaireSections(['image']);
      void handleImageSelect(file);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEdit, isVinted]);

  // ─── Validation ─────────────────────────────────────────────────────────
  // `vestiaireErrors` : erreurs du formulaire VC effectif à l'envoi (après un import implicite d'URL).
  const validate = (vestiaireErrors = vestiaireSearch.errors): boolean => {
    const next: Record<string, string> = {};
    if (!nom.trim()) next.nom = t('vendorSearch.nameError');

    // Vestiaire : erreurs de prix et de critère resserrant traduites et affichées par le panneau VC
    // (comme Vinted), dès que `submitAttempted` est posé. Les bornes serveur (mots-clés, ids) ne
    // bloquent plus la création : le serveur répond, l'erreur de création suit le même chemin que
    // Vinted (catch générique de handleSubmit) — elles ne servent plus qu'à ne pas lancer l'aperçu.
    if (isVestiaire) {
      setErrors(next);
      return !next.nom && !hasBlockingVestiaireSubmitError(vestiaireErrors);
    }

    const hasScope = categoriesPaths.length > 0 || marquesValue.length > 0 || motsCles.length > 0;
    if (!hasScope) next.categories = t('searchModal.errorScope');

    const min = numOrUndefined(prixMin);
    const max = numOrUndefined(prixMax);
    if (min != null && max != null && min >= max) {
      next.prixMin = t('searchModal.errorMinMax');
    }

    setErrors(next);

    // Auto-expand sections that have errors so the user sees them
    const errorSections: string[] = [];
    if (next.prixMin) errorSections.push('prix');
    if (next.categories) errorSections.push('categories');
    if (errorSections.length > 0) {
      setOpenSections((prev) => Array.from(new Set([...prev, ...errorSections])));
    }

    return Object.keys(next).length === 0;
  };

  // ─── Submit ─────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Mode édition : seul le nom change.
      if (isEdit) {
        if (!nom.trim()) {
          setErrors({ nom: t('vendorSearch.nameError') });
          return;
        }
        await onSubmit({ title: nom.trim(), platform });
        onClose();
        return;
      }

      // Téléversement d'image encore en cours : lancer maintenant créerait la
      // recherche SANS le critère image, silencieusement (Vinted et Vestiaire)
      if (imageUploading) {
        appMessage.warning(t('searchModal.waitForUpload'));
        return;
      }

      if (isVestiaire) {
        // URL collée mais pas importée : importée d'abord, comme Vinted ; envoi interrompu si elle
        // n'a pas pu être lue (erreur sous le champ, section rouverte).
        const draft = await vestiaireSearch.prepareSubmit();
        if (!draft) {
          requestVestiaireSections(['urlImport']);
          return;
        }
        setSubmitAttempted(true);
        if (!validate(draft.errors)) return;
        await onSubmit(
          {
            title: nom.trim(),
            folderId,
            platform,
            vestiaireCriteria: { ...draft.criteria, ...buildSimilarityCriteria(searchImage?.key, description) },
          },
          draft.fromUrl ? 'url' : 'manual',
        );
        onClose();
        return;
      }

      // URL collée mais pas importée : importée d'abord. Échec ⇒ envoi interrompu, erreur sous le
      // champ ; succès ⇒ envoi relancé au rendu suivant (`submitAfterImport`), sinon la création
      // partirait avec les critères d'avant l'import.
      if (vintedUrl.trim() && !urlImportUsed) {
        const imported = await handleUrlImport();
        if (imported) {
          setSubmitAfterImport(true);
        } else {
          setOpenSections((previous) => (previous.includes('urlImport') ? previous : [...previous, 'urlImport']));
        }
        return;
      }

      if (!validate()) return;

      await onSubmit({ title: nom.trim(), criteria, folderId, platform: 'vinted' }, urlImportUsed ? 'url' : 'manual');
      onClose();
    } catch (error) {
      console.error('Submit error:', error);
      const message = error instanceof Error ? error.message : '';
      // Quota mensuel atteint : popup avec CTA abonnement plutôt qu'un toast
      if (message === QUOTA_ERROR_MESSAGE) {
        setQuotaModalOpen(true);
      } else {
        appMessage.error(message || t('productSearch.createError'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Relance de l'envoi après un import automatique d'URL (cf `submitAfterImport`) : ce rendu porte les
  // critères importés. Référence à jour de `handleSubmit` pour ne pas relancer une closure périmée.
  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;
  useEffect(() => {
    if (!submitAfterImport) return;
    setSubmitAfterImport(false);
    void handleSubmitRef.current();
  }, [submitAfterImport]);

  const handleCancel = () => {
    setErrors({});
    setVintedUrl('');
    setUrlError('');
    setUrlImportUsed(false);
    onClose();
  };

  // ─── Tag renderers — custom spans bypassing AntD <Tag> styles ─────────
  const categoryTagRender = useCallback(
    (props: AntdTagRenderProps) => {
      const { label, closable, onClose: onTagClose } = props;
      return (
        <span className={dsCategoryTag} onMouseDown={(e) => e.stopPropagation()}>
          {label}
          {closable && (
            <button type="button" onClick={onTagClose} className={tagCloseBtn} aria-label={t('searchModal.removeTag')}>
              <X size={10} strokeWidth={2.25} />
            </button>
          )}
        </span>
      );
    },
    [t],
  );

  const brandTagRender = useCallback(
    (props: AntdTagRenderProps) => {
      const { label, closable, onClose: onTagClose } = props;
      return (
        <span className={dsBrandTag} onMouseDown={(e) => e.stopPropagation()}>
          {label}
          {closable && (
            <button type="button" onClick={onTagClose} className={tagCloseBtn} aria-label={t('searchModal.removeTag')}>
              <X size={10} strokeWidth={2.25} />
            </button>
          )}
        </span>
      );
    },
    [t],
  );

  const excludedTagRender = useCallback(
    (props: AntdTagRenderProps) => {
      const { label, closable, onClose: onTagClose } = props;
      return (
        <span className={dsExcludedTag} onMouseDown={(e) => e.stopPropagation()}>
          {label}
          {closable && (
            <button type="button" onClick={onTagClose} className={tagCloseBtn} aria-label={t('searchModal.removeTag')}>
              <X size={10} strokeWidth={2.25} />
            </button>
          )}
        </span>
      );
    },
    [t],
  );

  // ─── Chip filter defs — options SERVEUR : seuls les filtres possibles
  // pour les catégories choisies apparaissent (facettes getVintedCatalogFilters)
  const chipFilterDefs: {
    field: string;
    label: string;
    icon: ReactNode;
    value: string[];
    setter: (v: string[]) => void;
    options: SelectOption[];
  }[] = [
    {
      field: 'etats',
      label: t('searchModal.filters.etats'),
      icon: <Sparkles size={16} strokeWidth={1.75} />,
      value: etatsValue,
      setter: setEtatsValue,
      options: serverFilters.map.STATUS ?? [],
    },
    {
      field: 'tailles',
      label: t('searchModal.filters.tailles'),
      icon: <Ruler size={16} strokeWidth={1.75} />,
      value: taillesValue,
      setter: setTaillesValue,
      options: serverFilters.map.SIZE ?? [],
    },
    {
      field: 'couleurs',
      label: t('searchModal.filters.couleurs'),
      icon: <Palette size={16} strokeWidth={1.75} />,
      value: couleursValue,
      setter: setCouleursValue,
      options: serverFilters.map.COLOR ?? [],
    },
    {
      field: 'matieres',
      label: t('searchModal.filters.matieres'),
      icon: <Shirt size={16} strokeWidth={1.75} />,
      value: matieresValue,
      setter: setMatieresValue,
      options: serverFilters.map.MATERIAL ?? [],
    },
    {
      field: 'motifs',
      label: t('searchModal.filters.motifs'),
      icon: <Grid3x3 size={16} strokeWidth={1.75} />,
      value: motifsValue,
      setter: setMotifsValue,
      options: serverFilters.map.PATTERNS ?? [],
    },
    {
      field: 'modeles',
      label: t('searchModal.filters.modeles'),
      icon: <Package size={16} strokeWidth={1.75} />,
      value: modelesValue,
      setter: setModelesValue,
      options: serverFilters.map.BRAND_COLLECTION ?? [],
    },
    {
      field: 'plateformes',
      label: t('searchModal.filters.plateformes'),
      icon: <Gamepad2 size={16} strokeWidth={1.75} />,
      value: plateformesValue,
      setter: setPlateformesValue,
      options: serverFilters.map.VIDEO_GAME_PLATFORM ?? [],
    },
    {
      field: 'classements',
      label: t('searchModal.filters.classements'),
      icon: <ShieldCheck size={16} strokeWidth={1.75} />,
      value: classementsValue,
      setter: setClassementsValue,
      options: serverFilters.map.VIDEO_GAME_RATING ?? [],
    },
    {
      field: 'stockages',
      label: t('searchModal.filters.stockages'),
      icon: <HardDrive size={16} strokeWidth={1.75} />,
      value: stockagesValue,
      setter: setStockagesValue,
      options: serverFilters.map.INTERNAL_MEMORY_CAPACITY ?? [],
    },
    {
      field: 'langues',
      label: t('searchModal.filters.langues'),
      icon: <Languages size={16} strokeWidth={1.75} />,
      value: languesValue,
      setter: setLanguesValue,
      options: serverFilters.map.LANGUAGE_BOOK ?? [],
    },
    {
      field: 'simlocks',
      label: t('searchModal.filters.simlocks'),
      icon: <Lock size={16} strokeWidth={1.75} />,
      value: simlocksValue,
      setter: setSimlocksValue,
      options: serverFilters.map.SIM_LOCK ?? [],
    },
  ];

  // Quota mensuel serveur : coût 1 recherche, restant après lancement
  const creditsLine =
    credits.ready && credits.remaining.product === 0 ? (
      <span className={css({ fontSize: 'caption', color: 'error.default', fontWeight: 'medium' })}>
        {t('searchModal.quotaReached')}{' '}
        <button
          type="button"
          onClick={() => setQuotaModalOpen(true)}
          className={css({
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'error.default',
            fontSize: 'caption',
            fontWeight: 'medium',
            textDecoration: 'underline',
            padding: 0,
          })}
        >
          {t('searchModal.quotaReachedCta')}
        </button>
      </span>
    ) : credits.ready ? (
      <span className={css({ fontSize: 'caption', color: 'text.tertiary' })}>
        {t('relaunch.cost', { count: credits.remaining.product - 1 })}
      </span>
    ) : null;

  // ─── Section image + description, partagée par les deux onglets ─────────
  const similaritySection = (
    <SimilaritySearchSection
      searchImage={searchImage}
      imageUploading={imageUploading}
      onImageSelect={(file) => void handleImageSelect(file)}
      onRemoveImage={() => setSearchImage(null)}
      description={description}
      onDescriptionChange={setDescription}
      editImageKey={editImageKey}
      editImageUrl={editImageUrl}
      editSourceProductId={editSourceProductId}
      sourceProduct={
        sourceProduct ? { title: sourceProduct.title, imageUrl: sourceProduct.images?.[0]?.url ?? null } : null
      }
      initialImageUrl={initialImageUrl}
    />
  );

  // ─── Footer ─────────────────────────────────────────────────────────────
  const footer = (
    <>
      <Button variant="ghost" onClick={handleCancel} disabled={loading}>
        {t('common.cancel')}
      </Button>
      <Button variant="primary" handleClick={handleSubmit} showLoader>
        {isEdit ? t('common.save') : isVeille ? t('veille.start') : t('relaunch.confirm')}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title={
        isEdit
          ? isVeille
            ? t('searchModal.titleEditVeille')
            : t('searchModal.titleEdit')
          : isVeille
            ? t('searchModal.titleVeille')
            : t('searchModal.title')
      }
      width="1100px"
      height="720px"
      bodyPadding="none"
      footer={footer}
    >
      <div className={grid}>
        {/* ─── LEFT COLUMN — accordions (lecture seule en édition) ────── */}
        <div className={leftColumn}>
          {/* Plateforme : onglets (logo + nom) en création ; en édition l'onglet est figé (badge
              seul) ; jamais en variante veille. */}
          {!isVeille && !isEdit && (
            <div className={css({ display: 'flex', gap: '2', flexWrap: 'wrap' })}>
              <ChipToggle
                selected={isVinted}
                onChange={() => setPlatform('vinted')}
                icon={<PlatformLogo platform="vinted" />}
              >
                {t('vestiaireSearch.tabVinted')}
              </ChipToggle>
              {vestiaireLocked ? (
                <VestiaireLockedChip />
              ) : (
                <ChipToggle
                  selected={isVestiaire}
                  onChange={() => setPlatform('vestiaire')}
                  icon={<PlatformLogo platform="vestiaire" />}
                >
                  {t('vestiaireSearch.tabVestiaire')}
                </ChipToggle>
              )}
            </div>
          )}
          {!isVeille && isEdit && (
            <div className={css({ display: 'flex' })}>
              <PlatformBadge platform={platform} size="md" />
            </div>
          )}
          {isEdit && (
            <span className={css({ fontSize: 'caption', color: 'text.tertiary', fontStyle: 'italic' })}>
              {isVeille ? t('searchModal.editLockedVeille') : t('searchModal.editLocked')}
            </span>
          )}
          {isVestiaire ? (
            <VestiaireFilterPanel
              search={vestiaireSearch}
              readOnly={isEdit}
              showErrors={submitAttempted}
              similaritySection={similaritySection}
              openRequest={vestiaireOpenRequest}
            />
          ) : (
            <div className={isEdit ? css({ pointerEvents: 'none', opacity: 0.65 }) : undefined}>
              <Accordion multiple value={openSections} onValueChange={setOpenSections}>
                {/* URL Import — création uniquement */}
                {!isEdit && (
                  <Accordion.Item value="urlImport">
                    <Accordion.Header
                      icon={<LinkIcon size={16} strokeWidth={1.75} />}
                      label={t('searchModal.urlImportLabel')}
                    />
                    <Accordion.Content>
                      <div className={css({ display: 'flex', flexDirection: 'column', gap: '3' })}>
                        <span className={css({ fontSize: 'caption', color: 'text.tertiary' })}>
                          {t('searchModal.urlImportHint')}{' '}
                          <a
                            href="https://www.vinted.fr/catalog?page=1"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={css({
                              color: 'brand.primary',
                              textDecoration: 'underline',
                              _hover: { color: 'brand.primaryHover' },
                            })}
                          >
                            {t('searchModal.openVintedCatalog')}
                          </a>
                        </span>
                        <div className={inlineGap}>
                          <Input
                            placeholder="https://www.vinted.fr/catalog?catalog[]=…"
                            value={vintedUrl}
                            onChange={(e) => {
                              setVintedUrl(e.target.value);
                              setUrlError('');
                            }}
                            leading={<LinkIcon size={14} strokeWidth={1.75} />}
                            state={urlError ? 'error' : 'default'}
                          />
                          <Button
                            variant="secondary"
                            size="md"
                            onClick={async () => {
                              await handleUrlImport();
                            }}
                            disabled={!vintedUrl.trim()}
                            showLoader
                          >
                            {t('searchModal.import')}
                          </Button>
                        </div>
                        {urlError && <span className={errorText}>{urlError}</span>}
                      </div>
                    </Accordion.Content>
                  </Accordion.Item>
                )}

                {/* Catégories — AntD Cascader inside accordion */}
                <Accordion.Item value="categories">
                  <Accordion.Header
                    icon={<LayoutGrid size={16} strokeWidth={1.75} />}
                    label={t('searchModal.categories')}
                    count={categoriesPaths.length}
                  />
                  <Accordion.Content>
                    <div className={css({ display: 'flex', flexDirection: 'column', gap: '3' })}>
                      {isMobile ? (
                        <MobileCategoryDrilldown
                          options={categoryOptions}
                          value={categoriesPaths}
                          onChange={handleCategoriesChange}
                          loading={categoriesLoading}
                        />
                      ) : (
                        <Cascader
                          multiple
                          placeholder={t('searchModal.categoryPlaceholder')}
                          options={categoryOptions}
                          value={categoriesPaths}
                          onChange={(value) => handleCategoriesChange(value as string[][])}
                          showSearch
                          maxTagCount="responsive"
                          expandTrigger="hover"
                          tagRender={categoryTagRender}
                          loading={categoriesLoading}
                          style={{ width: '100%' }}
                        />
                      )}
                      {errors.categories && <span className={errorText}>{errors.categories}</span>}
                    </div>
                  </Accordion.Content>
                </Accordion.Item>

                {/* Marques — AntD Select multi avec recherche locale */}
                <Accordion.Item value="marques">
                  <Accordion.Header
                    icon={<Award size={16} strokeWidth={1.75} />}
                    label={t('searchModal.brands')}
                    count={marquesValue.length}
                  />
                  <Accordion.Content>
                    <div className={css({ display: 'flex', flexDirection: 'column', gap: '4' })}>
                      <AntdSelect
                        mode="multiple"
                        placeholder={t('searchModal.brandPlaceholder')}
                        options={brandOptions}
                        value={marquesValue}
                        onChange={setMarquesValue}
                        showSearch
                        filterOption={false}
                        notFoundContent={null}
                        tagRender={brandTagRender}
                        style={{ width: '100%' }}
                        onSearch={handleBrandSearch}
                      />
                      {/* Exclusion : garde les produits sans marque connue et
                      écarte les ids listés (excludedBrandIds) */}
                      <FormField label={t('searchModal.excludeBrands')}>
                        <AntdSelect
                          mode="multiple"
                          placeholder={t('searchModal.excludeBrandPlaceholder')}
                          options={brandOptions}
                          value={marquesExcluesValue}
                          onChange={setMarquesExcluesValue}
                          showSearch
                          filterOption={false}
                          notFoundContent={null}
                          tagRender={excludedTagRender}
                          style={{ width: '100%' }}
                          onSearch={handleBrandSearch}
                        />
                      </FormField>
                    </div>
                  </Accordion.Content>
                </Accordion.Item>

                {/* Pays du vendeur (2026-09-02) — menu groupé par région : cocher
                une région coche tous ses pays, mais l'input envoyé reste une
                liste de pays à plat. Masqué tant que getVintedCountries ne
                répond pas (back pas encore déployé). */}
                {countryRegions.length > 0 && (
                  <Accordion.Item value="pays">
                    <Accordion.Header
                      icon={<Globe size={16} strokeWidth={1.75} />}
                      label={t('searchModal.sellerCountry')}
                      count={paysValue.length}
                    />
                    <Accordion.Content>
                      <div className={css({ display: 'flex', flexDirection: 'column', gap: '5' })}>
                        <span className={css({ fontSize: 'caption', color: 'text.tertiary', lineHeight: 1.5 })}>
                          {t('searchModal.countryHint')}
                        </span>
                        {countryRegions.map((group) => {
                          const groupIds = group.countries.map((c) => String(c.id));
                          const allChecked = groupIds.every((id) => paysValue.includes(id));
                          const REGION_LABELS: Record<string, string> = {
                            europeWest: t('sellers.regions.europeWest'),
                            europeEast: t('sellers.regions.europeEast'),
                            unitedKingdom: t('sellers.regions.unitedKingdom'),
                            northAmerica: t('sellers.regions.northAmerica'),
                          };
                          return (
                            <div
                              key={group.region}
                              className={css({ display: 'flex', flexDirection: 'column', gap: '2.5' })}
                            >
                              <div className={css({ display: 'flex', alignItems: 'center', gap: '3' })}>
                                <span
                                  className={css({
                                    fontSize: 'caption',
                                    fontWeight: 'semibold',
                                    color: 'text.secondary',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                  })}
                                >
                                  {REGION_LABELS[group.region] ?? group.region}
                                </span>
                                <button
                                  type="button"
                                  disabled={isEdit}
                                  onClick={() =>
                                    setPaysValue(
                                      allChecked
                                        ? paysValue.filter((id) => !groupIds.includes(id))
                                        : [...new Set([...paysValue, ...groupIds])],
                                    )
                                  }
                                  className={css({
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: 'micro',
                                    color: 'brand.primary',
                                    textDecoration: 'underline',
                                    padding: 0,
                                    _disabled: { color: 'text.tertiary', cursor: 'default', textDecoration: 'none' },
                                  })}
                                >
                                  {allChecked ? t('searchModal.uncheckAll') : t('searchModal.wholeRegion')}
                                </button>
                              </div>
                              <div className={css({ display: 'flex', flexWrap: 'wrap', gap: '2' })}>
                                {group.countries.map((country) => {
                                  const id = String(country.id);
                                  const selected = paysValue.includes(id);
                                  return (
                                    <ChipToggle
                                      key={id}
                                      selected={selected}
                                      onChange={() => {
                                        if (isEdit) return;
                                        setPaysValue(selected ? paysValue.filter((v) => v !== id) : [...paysValue, id]);
                                      }}
                                    >
                                      {localizeCountryName(country.isoCode, country.name)}
                                    </ChipToggle>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Accordion.Content>
                  </Accordion.Item>
                )}

                {/* Recherche par image — section partagée avec le panneau Vestiaire */}
                {similaritySection}

                {/* Filtres à chips — facettes serveur : les combinaisons possibles
                pour les catégories choisies */}
                {selectedCatalogIds.length === 0 ? (
                  <div className={css({ paddingY: '4', paddingX: '2' })}>
                    <span className={css({ fontSize: 'caption', color: 'text.tertiary' })}>
                      {t('searchModal.chooseCategoryHint')}
                    </span>
                  </div>
                ) : catalogFiltersLoading ? (
                  <div className={css({ display: 'flex', justifyContent: 'center', paddingY: '5' })}>
                    <Spinner size={18} />
                  </div>
                ) : (
                  chipFilterDefs
                    .filter((def) => def.options.length > 0)
                    .map((def) => (
                      <Accordion.Item key={def.field} value={def.field}>
                        <Accordion.Header icon={def.icon} label={def.label} count={def.value.length} />
                        <Accordion.Content>
                          <ChipMultiPicker
                            options={def.options}
                            value={def.value}
                            onChange={def.setter}
                            searchPlaceholder={t('searchModal.filterSearchPlaceholder', {
                              label: def.label.toLowerCase(),
                            })}
                            emptyHint={t('searchModal.filterEmptyHint', { label: def.label.toLowerCase() })}
                            dotFor={def.field === 'couleurs' ? (opt) => serverFilters.hexById[opt.value] : undefined}
                          />
                        </Accordion.Content>
                      </Accordion.Item>
                    ))
                )}

                {/* Prix */}
                <Accordion.Item value="prix">
                  <Accordion.Header
                    icon={<Euro size={16} strokeWidth={1.75} />}
                    label={t('segment.price')}
                    count={(prixMin ? 1 : 0) + (prixMax ? 1 : 0)}
                  />
                  <Accordion.Content>
                    <div className={css({ display: 'flex', flexDirection: 'column', gap: '3' })}>
                      <div className={css({ display: 'flex', gap: '3' })}>
                        <Input
                          type="number"
                          inputMode="decimal"
                          placeholder={t('searchModal.min')}
                          value={prixMin}
                          onChange={(e) => setPrixMin(e.target.value)}
                          trailing={<span className={css({ color: 'text.tertiary', fontSize: 'caption' })}>€</span>}
                        />
                        <Input
                          type="number"
                          inputMode="decimal"
                          placeholder={t('searchModal.max')}
                          value={prixMax}
                          onChange={(e) => setPrixMax(e.target.value)}
                          trailing={<span className={css({ color: 'text.tertiary', fontSize: 'caption' })}>€</span>}
                        />
                      </div>
                      {errors.prixMin && <span className={errorText}>{errors.prixMin}</span>}
                    </div>
                  </Accordion.Content>
                </Accordion.Item>

                {/* Mots-clés → search_text Vinted */}
                <Accordion.Item value="motsCles">
                  <Accordion.Header
                    icon={<Hash size={16} strokeWidth={1.75} />}
                    label={t('searchModal.keywords')}
                    count={motsCles.length + motsClesExclus.length}
                  />
                  <Accordion.Content>
                    <div className={css({ display: 'flex', flexDirection: 'column', gap: '4' })}>
                      <FormField label={t('searchModal.textSearch')} hint={t('searchModal.keywordsHint')}>
                        <AntdSelect
                          mode="tags"
                          placeholder={t('searchModal.keywordPlaceholder')}
                          tokenSeparators={[',']}
                          value={motsCles}
                          onChange={setMotsCles}
                          style={{ width: '100%' }}
                        />
                      </FormField>

                      {/* Opérateur d'inclusion — ET par défaut (comportement
                      historique) ; ne s'applique jamais aux mots exclus */}
                      {motsCles.length > 1 && (
                        <div className={css({ display: 'flex', gap: '2', alignItems: 'center' })}>
                          <ChipToggle selected={motsClesOperator === 'and'} onChange={() => setMotsClesOperator('and')}>
                            {t('searchModal.keywordsAnd')}
                          </ChipToggle>
                          <ChipToggle selected={motsClesOperator === 'or'} onChange={() => setMotsClesOperator('or')}>
                            {t('searchModal.keywordsOr')}
                          </ChipToggle>
                        </div>
                      )}

                      <FormField label={t('searchModal.excludedKeywords')} hint={t('searchModal.excludedKeywordsHint')}>
                        <AntdSelect
                          mode="tags"
                          placeholder={t('searchModal.excludedKeywordPlaceholder')}
                          tokenSeparators={[',']}
                          value={motsClesExclus}
                          onChange={setMotsClesExclus}
                          tagRender={excludedTagRender}
                          style={{ width: '100%' }}
                        />
                      </FormField>
                    </div>
                  </Accordion.Content>
                </Accordion.Item>
              </Accordion>
            </div>
          )}
        </div>

        {/* ─── RIGHT COLUMN — detached card on different bg ─────────── */}
        <div className={rightColumn}>
          <div className={rightCard}>
            <FormField
              label={isVeille ? t('veille.nameLabel') : t('relaunch.nameLabel')}
              hint={errors.nom || undefined}
              hintTone={errors.nom ? 'error' : 'neutral'}
            >
              <Input
                placeholder={isVeille ? t('veille.namePlaceholder') : t('searchModal.namePlaceholder')}
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                state={errors.nom ? 'error' : 'default'}
                autoFocus
              />
            </FormField>

            {/* Affectation à un dossier — comme dans Autocop. Les favoris
                sont la catégorie n°1. En édition le dossier se gère par
                drag & drop dans l'historique. */}
            {!isEdit && !isVeille && (
              <FormField label={t('relaunch.folderLabel')} icon={<FolderIcon size={14} strokeWidth={1.75} />}>
                <Select
                  value={folderId ?? '__none__'}
                  onChange={(value) => setFolderId(value === '__none__' ? null : value)}
                  options={folderOptions}
                  placeholder={t('relaunch.noFolder')}
                />
              </FormField>
            )}

            {/* Aperçu d'URL Vinted (en édition : l'URL d'origine, figée) */}
            {isVinted && (
              <FormField label={t('searchModal.urlPreview')} icon={<LinkIcon size={14} strokeWidth={1.75} />}>
                <UrlPreview
                  url={generatedUrl}
                  onCopy={() => {
                    navigator.clipboard.writeText(generatedUrl ?? DEFAULT_VINTED_URL);
                    appMessage.success(t('searchModal.urlCopied'));
                  }}
                />
              </FormField>
            )}

            {/* Aperçu d'URL Vestiaire : générée depuis le formulaire (ids seuls), mêmes conditions que
                Vinted ; les filtres sans équivalent sur le site sont nommés sous l'encart */}
            {isVestiaire && (
              <FormField label={t('searchModal.urlPreview')} icon={<LinkIcon size={14} strokeWidth={1.75} />}>
                <UrlPreview
                  url={vestiaireUrl.url}
                  onCopy={() => {
                    navigator.clipboard.writeText(vestiaireUrl.url);
                    appMessage.success(t('searchModal.urlCopied'));
                  }}
                />
                {vestiaireUrl.omitted.length > 0 && (
                  <span className={css({ fontSize: 'caption', color: 'text.tertiary' })}>
                    {t('vestiaireSearch.urlNotRepresented', { parts: omissionsText(vestiaireUrl.omitted, t) })}
                  </span>
                )}
              </FormField>
            )}

            {/* Aperçu Vestiaire : même boîte que Vinted, avec la ligne de quota commune
                (quota de recherches partagé entre les plateformes) */}
            {!isEdit && !isVeille && isVestiaire && (
              <div className={previewBox}>
                <VestiaireMatchPreview
                  criteria={vestiaireCriteria}
                  errors={vestiaireSearch.errors}
                  enabled={isOpen && !isEdit}
                />
                {creditsLine}
              </div>
            )}

            {/* Aperçu du volume détecté — se met à jour après 1 s sans
                modification des filtres (previewAnalysisMatchCount) */}
            {!isEdit && !isVeille && isVinted && (
              <div className={previewBox}>
                {!hasScopeNow ? (
                  <span className={css({ fontSize: 'caption', color: 'text.tertiary' })}>
                    {t('searchModal.previewScopeHint')}
                  </span>
                ) : (
                  <span
                    className={css({
                      fontFamily: 'mono',
                      fontSize: '17px',
                      lineHeight: '1.15',
                      fontWeight: 'bold',
                      color: 'text.primary',
                      letterSpacing: '-0.4px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '3',
                    })}
                  >
                    {previewLoading || matchPreview == null || previewCount == null ? (
                      <Spinner size={14} />
                    ) : (
                      // similarityPending : l'aperçu n'applique jamais la
                      // similarité -> le tirage est un MAJORANT (« ≤ »)
                      `${previewPrefix}${previewCount.toLocaleString(i18n.language?.startsWith('en') ? 'en-GB' : 'fr-FR')}`
                    )}
                    {matchPreview != null && !previewLoading && (
                      <span className={css({ fontSize: 'bodySm', fontWeight: 'medium', color: 'text.secondary' })}>
                        {matchPreview.similarityPending
                          ? t('searchModal.previewBeforeSimilarity')
                          : matchPreview.exhaustive
                            ? // Le nombre est le tirage EXACT : « analysés » et non
                              // « détectés » — le périmètre en contient bien plus.
                              t('searchModal.previewAnalysed')
                            : t('searchModal.previewDetected')}
                      </span>
                    )}
                  </span>
                )}
                {creditsLine}
              </div>
            )}

            {loading && (
              <div className={css({ display: 'flex', alignItems: 'center', gap: '3', color: 'text.tertiary' })}>
                <Spinner size={14} /> <span className={css({ fontSize: 'caption' })}>{t('searchModal.launching')}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      <QuotaReachedModal isOpen={quotaModalOpen} kind="product" onClose={() => setQuotaModalOpen(false)} />
    </Modal>
  );
};
